import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "motion/react";
import { imagineStore } from "@/stores/imagine.store";
import { imagineConfigStore } from "@/stores/imagine-config.store";
import ProgressHeaderTexture from "@/images/in-chat-progress-header-texture.png";
import { useTheme } from "next-themes";

export type UsageLimitExceededCalloutProps = {
  className?: string;
};

export function UsageLimitExceededCallout({
  className,
}: UsageLimitExceededCalloutProps) {
  const hasExceededUsageLimit =
    imagineStore.use.billing.hasExceededUsageLimit();
  const isRunningLow = imagineStore.use.billing.isRunningLow();
  const { resolvedTheme: theme } = useTheme();

  const handleUpgrade = async () => {
    const callbacks = imagineConfigStore.getState().callbacks;
    await callbacks?.onUpgrade?.();
  };

  // Don't show if not running low and not exceeded
  if (!isRunningLow && !hasExceededUsageLimit) {
    return null;
  }

  const headerBg = "bg-black dark:bg-white";

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={hasExceededUsageLimit ? "exceeded" : "low"}
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: "auto", opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{
          duration: 0.3,
          ease: [0.32, 0.72, 0, 1],
        }}
        className={cn(headerBg, "rounded-t-[20px] mb-[-16px]", className)}
      >
        <div
          className={cn(
            "h-8.5 flex flex-col rounded-t-[20px] overflow-hidden",
            headerBg,
          )}
        >
          <div className="flex-1 flex items-center px-4 relative text-sidebar">
            <div className="relative z-10 w-full flex justify-between items-center text-[0.75rem]">
              <div className="tracking-tight flex items-center gap-2">
                <span>
                  {hasExceededUsageLimit
                    ? "You've run out of credits."
                    : "Running out of credits."}
                </span>
                <button
                  onClick={handleUpgrade}
                  className="font-medium hover:underline underline-offset-2 underline cursor-pointer"
                >
                  Upgrade to continue
                </button>
              </div>
            </div>
            <div className="absolute right-0 top-0 w-full h-full pointer-events-none">
              <img
                src={ProgressHeaderTexture}
                alt="Progress Header Texture"
                className={cn(
                  "absolute right-0 top-0 h-full object-cover",
                  hasExceededUsageLimit ? "opacity-50" : "opacity-100",
                )}
                style={{
                  filter: theme === "dark" ? "invert(100%)" : undefined,
                  maskImage:
                    theme === "dark"
                      ? "linear-gradient(to right, transparent 0%, white 40%, white 100%)"
                      : "linear-gradient(to right, transparent 0%, black 40%, black 100%)",
                }}
              />
            </div>
          </div>
        </div>
        <div className={cn("rounded-b-5 h-4", headerBg)} />
      </motion.div>
    </AnimatePresence>
  );
}
